'use client';

import { useState, type KeyboardEvent } from 'react';
import Link from 'next/link';
import { Send, Square } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { classifyLink } from '@/lib/assistant/links';
import { composerBlock, quotaMeter } from '@/lib/assistant/session';
import { cn } from '@/lib/utils';
import type { AgentStatus } from '@/lib/api/types';

/** The backend rejects anything longer with a 422 (`schemas/agent.py`). */
const MAX_TEXT_LENGTH = 4000;
/** Start showing the character count once a message is this close to the cap. */
const LENGTH_WARNING_THRESHOLD = MAX_TEXT_LENGTH - 200;

export function Composer({
  status,
  streaming,
  onSend,
  onStop,
}: {
  status: AgentStatus | undefined;
  streaming: boolean;
  onSend: (text: string) => void;
  onStop: () => void;
}) {
  const [text, setText] = useState('');
  const blocked = composerBlock(status);
  const meter = status ? quotaMeter(status.quota) : null;
  const trimmed = text.trim();
  // The textarea's `maxLength` stops typed/pasted-with-IME input at the cap,
  // but a scripted paste can still land a longer value in state — the guard
  // has to be re-checked here, not just declared on the DOM node.
  const overLimit = text.length > MAX_TEXT_LENGTH;
  const canSend = !blocked && !streaming && trimmed.length > 0 && !overLimit;
  const showCount = text.length >= LENGTH_WARNING_THRESHOLD;

  const submit = () => {
    if (!canSend) return;
    onSend(trimmed);
    setText('');
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter sends; Shift+Enter is a newline. Do not hijack an IME commit.
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      submit();
    }
  };

  // The same same-origin/allow-list logic the transcript already applies to
  // model-authored links (`lib/assistant/links.ts`): `upgrade_url` comes from
  // this workspace's own backend, not from the model, but it still deserves
  // the same "never hand an unvetted scheme to <a>" discipline, and reusing it
  // means an internal URL becomes a client-side `<Link>` instead of a full
  // reload.
  const origin = typeof window === 'undefined' ? '' : window.location.origin;
  const upgradeTarget = blocked?.upgradeUrl ? classifyLink(blocked.upgradeUrl, origin) : null;

  return (
    <div className="shrink-0 border-t p-2">
      {blocked && (
        <p className="mb-2 rounded-md bg-muted px-2 py-1.5 text-xs text-muted-foreground">
          {blocked.message}{' '}
          {upgradeTarget?.kind === 'internal' && (
            <Link href={upgradeTarget.path} className="underline underline-offset-2">
              See your plan
            </Link>
          )}
          {upgradeTarget?.kind === 'external' && (
            <a
              href={upgradeTarget.href}
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-2"
            >
              See your plan
            </a>
          )}
        </p>
      )}

      {meter && (
        <p className="mb-1.5 text-[11px] text-muted-foreground">
          {meter.used} / {meter.limit} messages this month
        </p>
      )}

      <div className="flex items-end gap-2">
        <div className="flex-1">
          <Textarea
            value={text}
            onChange={(event) => setText(event.target.value)}
            onKeyDown={onKeyDown}
            disabled={Boolean(blocked)}
            maxLength={MAX_TEXT_LENGTH}
            rows={1}
            placeholder="Ask the assistant…"
            aria-label="Message the assistant"
            className="max-h-32 min-h-9 resize-none text-sm"
          />
          {showCount && (
            <p
              className={cn(
                'mt-1 text-right text-[11px]',
                overLimit ? 'text-destructive' : 'text-muted-foreground',
              )}
            >
              {text.length} / {MAX_TEXT_LENGTH}
            </p>
          )}
        </div>
        {streaming ? (
          <Button size="sm" variant="outline" onClick={onStop} aria-label="Stop the assistant">
            <Square className="size-3.5" />
          </Button>
        ) : (
          <Button size="sm" disabled={!canSend} onClick={submit} aria-label="Send">
            <Send className="size-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
}
